/**
 * Ce que le visiteur voit d'un point d'intérêt dans le panneau de la carte publiée.
 *
 * Les règles vivent ici et pas dans l'écran qui les applique, parce qu'elles sont appliquées à DEUX
 * endroits à partir de sources différentes : la prévisualisation grandeur nature (`PreviewModal`,
 * qui part du JSON de la carte) et l'aperçu de la fiche d'un point d'intérêt (`PlaceForm`, qui part
 * des valeurs du formulaire, avant même qu'elles soient publiées). Deux copies auraient fini par ne
 * plus dire la même chose, et l'aperçu aurait menti — ce qui est le seul défaut qu'un aperçu ne peut
 * pas se permettre.
 */

const PANEL_FIELD_KINDS: Partial<Record<Map_FieldTypeReference, PlacePanelField['kind']>> = {
	color: 'color',
	url: 'url',
};

/** Champ dont la valeur REMPLACE le titre du panneau, au lieu de s'afficher comme les autres. */
export const TITLE_FIELD_REFERENCE = 'title';

export type PlacePanelField = { label: string; value: string; kind?: 'text' | 'image' | 'color' | 'url'; };

export type PlacePanelEntry = {
	title: string;
	subTitle?: string;
	fields: PlacePanelField[];
};

export type PlacePanelInput = {
	label?: string;
	location?: string;
	reference?: string;
	fields: {
		label: string;
		/** Référence du champ (`FieldModel.reference`) — `title` a un effet particulier. */
		reference?: string;
		/** Référence de la NATURE (`FieldTypeModel.reference`) — la valeur d'un `image` est une URL. */
		fieldType?: string;
		value: unknown;
	}[];
};

/**
 * Valeur affichable d'un champ, ou `null` s'il n'y a rien à montrer.
 *
 * Trois formes possibles selon la source : une chaîne (valeur simple), un objet de traductions venu
 * de l'API (`{ fr, en }`), ou le même objet tel que le formulaire le tient, où chaque langue est
 * elle-même un objet (`{ fr: { value } }`). Cette application étant en français, `fr` prime sur `en`.
 */
export function resolvePlaceFieldValue(raw: unknown): string | null {
	if (raw === null || raw === undefined) return null;
	if (typeof raw === 'string') return raw.trim() || null;

	if (typeof raw === 'object') {
		const translations = raw as Record<string, unknown>;
		const picked = translations.fr ?? translations.en;

		if (typeof picked === 'string') return picked.trim() || null;

		if (picked && typeof picked === 'object') {
			const value = (picked as { value?: unknown }).value;

			return typeof value === 'string' ? value.trim() || null : null;
		}

		return null;
	}

	return String(raw);
}

/**
 * Applique les trois règles du panneau public :
 *
 *  - un champ de référence `title` REMPLACE le titre au lieu de s'afficher dans la liste ;
 *  - les champs de nature `image` affichent l'image dont ils portent l'URL, ceux de nature `color`
 *    une pastille de leur couleur, ceux de nature `url` un lien ;
 *  - les champs sans valeur disparaissent — le visiteur ne voit jamais un intitulé vide.
 */
export default function buildPlacePanelEntry(input: PlacePanelInput): PlacePanelEntry {
	let title = input.label || '';
	const fields: PlacePanelField[] = [];

	input.fields.forEach(field => {
		const value = resolvePlaceFieldValue(field.value);

		if (!value) return;

		if (field.fieldType === 'image') {
			fields.push({ label: field.label, value, kind: 'image' });
			return;
		}

		if (field.reference?.toLowerCase() === TITLE_FIELD_REFERENCE) {
			title = value;
			return;
		}

		const kind = PANEL_FIELD_KINDS[field.fieldType as Map_FieldTypeReference];

		fields.push(kind ? { label: field.label, value, kind } : { label: field.label, value });
	});

	return { title, subTitle: input.location || input.reference, fields };
}

/** Les champs, les images qui se suivent réunies dans un même groupe — le panneau les aligne. */
export function groupConsecutiveImages(fields: PlacePanelField[]): PlacePanelField[][] {
	const groups: PlacePanelField[][] = [];

	fields.forEach(field => {
		const last = groups[groups.length - 1];

		if (field.kind === 'image' && last?.[0]?.kind === 'image') last.push(field);
		else groups.push([field]);
	});

	return groups;
}

/**
 * Valeur d'exemple pour une nature de champ, destinée aux aperçus qui n'ont pas de vraies données —
 * la fiche d'un TYPE, où l'on règle quels champs existent avant qu'aucun point d'intérêt ne les ait
 * remplis.
 *
 * `image` renvoie `null` : il n'y a pas d'image d'exemple à montrer.
 */
export function sampleFieldValue(fieldType?: string): string | null {
	switch (fieldType) {
		case 'number': return '42';
		case 'url': return 'https://exemple.fr';
		case 'color': return '#E62E7A';
		case 'image': return null;
		default: return 'Valeur d\'exemple';
	}
}
